import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DkimSelectors } from '../types/models/DkimSelector';
import type { ApiResponseDkimSelectors } from '../types/api/ApiResponseDkimSelectors';

interface UseDkimSelectorsReturn {
  data: DkimSelectors | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Reads the DKIM selectors configured on the mail domain's resolved IMAP host.
 *
 * The backend resolves this over SSH (`opendkim-manager`), so it is an expensive read — only call it
 * from a tab mounted lazily via `mountOnEnter`, never at page level. Per the API contract the response
 * is JSend wrapped and controller exceptions map to `status: 'error'` at HTTP 200, so we branch on
 * `body.status === 'success'` (+ the `dkim_selectors` key), not `response.ok`. Previous data is kept
 * during a refetch so the table stays visible while the Refresh button shows its loading state.
 */
export const useDkimSelectors = (mailDomainId: number | undefined): UseDkimSelectorsReturn => {
  const [data, setData] = useState<DkimSelectors | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchSelectors = useCallback(async () => {
    if (!mailDomainId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/emails/domains/${mailDomainId}/dkim/selectors`);
      const body = (await response.json()) as ApiResponseDkimSelectors;

      if (body.status === 'success' && body.data?.dkim_selectors) {
        setData(body.data.dkim_selectors);
      } else {
        setError(body.message ?? 'Failed to load DKIM selectors');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading DKIM selectors');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, mailDomainId]);

  useEffect(() => {
    if (auth.isAuthenticated && mailDomainId) {
      fetchSelectors();
    }
  }, [auth.isAuthenticated, mailDomainId, fetchSelectors]);

  return { data, loading, error, refetch: fetchSelectors };
};
